import React from 'react';
import { Presentation, PieChart, FileText, BarChart3, Table2, Share2 } from 'lucide-react';

export const TEMPLATES = {
  presentation: [
    { id: 'p1', name: 'Corporate Pitch', desc: 'Professional business pitch deck' },
    { id: 'p2', name: 'Startup Deck', desc: 'Modern startup fundraising deck' },
    { id: 'p3', name: 'Quarterly Review', desc: 'Business performance review' },
    { id: 'p4', name: 'Product Launch', desc: 'New product introduction' },
    { id: 'p5', name: 'Training Module', desc: 'Educational workshop slides' },
  ],
  infographic: [
    { id: 'i1', name: 'Data Story', desc: 'Stats and figures visualization' },
    { id: 'i2', name: 'Timeline', desc: 'Chronological process flow' },
    { id: 'i3', name: 'Comparison', desc: 'Side-by-side analysis' },
    { id: 'i4', name: 'Process Flow', desc: 'Step-by-step breakdown' },
  ],
  document: [
    { id: 'd1', name: 'Business Report', desc: 'Formal report with sections' },
    { id: 'd2', name: 'Proposal', desc: 'Client-facing project proposal' },
    { id: 'd3', name: 'Executive Brief', desc: 'Concise leadership summary' },
    { id: 'd4', name: 'Research Paper', desc: 'Academic-style document' },
  ],
  dashboard: [
    { id: 'db1', name: 'Sales Dashboard', desc: 'Revenue and pipeline metrics' },
    { id: 'db2', name: 'Marketing Analytics', desc: 'Campaign performance overview' },
    { id: 'db3', name: 'Project Tracker', desc: 'Team progress and milestones' },
    { id: 'db4', name: 'Financial Overview', desc: 'P&L and budget tracking' },
  ],
  sheets: [
    { id: 's1', name: 'Budget Planner', desc: 'Income and expense tracker' },
    { id: 's2', name: 'Sales Tracker', desc: 'Deal pipeline spreadsheet' },
    { id: 's3', name: 'Inventory Log', desc: 'Stock and supply tracking' },
    { id: 's4', name: 'Survey Results', desc: 'Response data analysis' },
  ],
};

const MODE_COLORS = {
  presentation: { bg: 'bg-green-50 dark:bg-green-950/20', border: 'border-green-200 dark:border-green-800/40', accent: 'bg-green-500', text: 'text-green-600 dark:text-green-400' },
  infographic: { bg: 'bg-purple-50 dark:bg-purple-950/20', border: 'border-purple-200 dark:border-purple-800/40', accent: 'bg-purple-500', text: 'text-purple-600 dark:text-purple-400' },
  document: { bg: 'bg-blue-50 dark:bg-blue-950/20', border: 'border-blue-200 dark:border-blue-800/40', accent: 'bg-blue-500', text: 'text-blue-600 dark:text-blue-400' },
  dashboard: { bg: 'bg-amber-50 dark:bg-amber-950/20', border: 'border-amber-200 dark:border-amber-800/40', accent: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400' },
  sheets: { bg: 'bg-teal-50 dark:bg-teal-950/20', border: 'border-teal-200 dark:border-teal-800/40', accent: 'bg-teal-500', text: 'text-teal-600 dark:text-teal-400' },
};

const MODE_ICONS = {
  presentation: Presentation,
  infographic: PieChart,
  document: FileText,
  dashboard: BarChart3,
  sheets: Table2,
};

export default function TemplateGallery({ mode, onSelectTemplate }) {
  const templates = TEMPLATES[mode];
  if (!templates || mode === 'consultant') return null;

  const colors = MODE_COLORS[mode] || MODE_COLORS.presentation;
  const Icon = MODE_ICONS[mode] || Presentation;

  return (
    <div className="max-w-6xl mx-auto mt-6">
      <p className="text-sm text-muted-foreground mb-3 flex items-center gap-2">
        <Icon className="w-4 h-4" />
        Templates
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {templates.map(t => (
          <button
            key={t.id}
            onClick={() => onSelectTemplate?.(t)}
            className={`group relative rounded-xl border ${colors.border} ${colors.bg} text-left transition-all duration-200 hover:shadow-md hover:scale-[1.02] p-3`}
          >
            {/* Placeholder thumbnail */}
            <div className="h-16 rounded-lg bg-white/60 dark:bg-gray-800/60 border border-gray-200/50 dark:border-gray-700/50 mb-2 flex items-center justify-center">
              <Icon className="w-6 h-6 text-gray-300 dark:text-gray-600 group-hover:scale-110 transition-transform" />
            </div>
            <h4 className="text-xs font-semibold text-foreground truncate">{t.name}</h4>
            <p className="text-[10px] text-muted-foreground truncate mt-0.5">{t.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
